import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  Brain, Puzzle, Cpu, Eye, Zap, Gamepad2, Gamepad, Square, Palette,
  BookOpen, Calculator, Target, Music, Map, Clock
} from 'lucide-react';
import { getRecentGames, recordRecentGame } from '../lib/recentGames';
import { getAchievements, getGameStats } from '../lib/gameStats';

const GamesSection = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [activeCategory, setActiveCategory] = useState('all');
  const recentGames = getRecentGames();
  const achievements = getAchievements().filter((a) => a.unlocked);

  const games = [
    { id: 'memory', category: 'brain', icon: <Brain className="h-10 w-10" />, title: t('games.memory'), description: t('games.memory.desc'), difficulty: t('game.difficulty.easy') },
    { id: 'puzzle', category: 'puzzle', icon: <Puzzle className="h-10 w-10" />, title: t('games.puzzle'), description: t('games.puzzle.desc'), difficulty: t('game.difficulty.medium') },
    { id: 'logic', category: 'brain', icon: <Cpu className="h-10 w-10" />, title: t('games.logic'), description: t('games.logic.desc'), difficulty: t('game.difficulty.hard') },
    { id: 'pattern', category: 'brain', icon: <Eye className="h-10 w-10" />, title: t('games.pattern'), description: t('games.pattern.desc'), difficulty: t('game.difficulty.medium') },
    { id: 'speed', category: 'arcade', icon: <Zap className="h-10 w-10" />, title: t('games.speed'), description: t('games.speed.desc'), difficulty: t('game.difficulty.easy') },
    { id: 'dino', category: 'arcade', icon: <Gamepad2 className="h-10 w-10" />, title: t('games.dino'), description: t('games.dino.desc'), difficulty: t('game.difficulty.easy') },
    { id: 'snake', category: 'arcade', icon: <Gamepad className="h-10 w-10" />, title: t('games.snake'), description: t('games.snake.desc'), difficulty: t('game.difficulty.medium') },
    { id: 'tetris', category: 'arcade', icon: <Square className="h-10 w-10" />, title: t('games.tetris'), description: t('games.tetris.desc'), difficulty: t('game.difficulty.hard') },
    { id: 'game2048', category: 'puzzle', icon: <Calculator className="h-10 w-10" />, title: t('games.game2048'), description: t('games.game2048.desc'), difficulty: t('game.difficulty.medium') },
    { id: 'colormatch', category: 'brain', icon: <Palette className="h-10 w-10" />, title: t('games.colormatch'), description: t('games.colormatch.desc'), difficulty: t('game.difficulty.easy') },
    { id: 'wordsearch', category: 'puzzle', icon: <BookOpen className="h-10 w-10" />, title: t('games.wordsearch'), description: t('games.wordsearch.desc'), difficulty: t('game.difficulty.medium') },
    { id: 'math', category: 'brain', icon: <Calculator className="h-10 w-10" />, title: t('games.math'), description: t('games.math.desc'), difficulty: t('game.difficulty.hard') },
    { id: 'reaction', category: 'arcade', icon: <Target className="h-10 w-10" />, title: t('games.reaction'), description: t('games.reaction.desc'), difficulty: t('game.difficulty.medium') },
    { id: 'rhythm', category: 'arcade', icon: <Music className="h-10 w-10" />, title: t('games.rhythm'), description: t('games.rhythm.desc'), difficulty: t('game.difficulty.hard') },
    { id: 'maze', category: 'puzzle', icon: <Map className="h-10 w-10" />, title: t('games.maze'), description: t('games.maze.desc'), difficulty: t('game.difficulty.medium') }
  ];

  const categories = [
    { id: 'all', label: t('games.filter.all') },
    { id: 'puzzle', label: t('games.filter.puzzle') },
    { id: 'arcade', label: t('games.filter.arcade') },
    { id: 'brain', label: t('games.filter.brain') }
  ];

  const filteredGames = useMemo(() => {
    const list = activeCategory === 'all' ? games : games.filter((g) => g.category === activeCategory);
    return list;
  }, [activeCategory, t]);

  const handleGameClick = (game) => {
    recordRecentGame(game.id, game.title);
    if (window.trackGameStart) {
      window.trackGameStart(game.id);
    }
    navigate(`/game/${game.id}`);
  };

  const recentWithMeta = recentGames
    .map((recent) => {
      const game = games.find((g) => g.id === recent.id);
      if (!game) return null;
      const stats = getGameStats(game.id);
      return { ...game, stats };
    })
    .filter(Boolean);

  return (
    <section id="games" className="py-12 md:py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-8"
        >
          <h2 className="text-2xl md:text-3xl font-bold mb-3 tracking-tight" style={{ color: 'var(--text-primary)' }}>
            {t('games.title')}
          </h2>
          <p className="text-lg max-w-2xl mx-auto" style={{ color: 'var(--text-secondary)' }}>
            {t('games.subtitle')}
          </p>
        </motion.div>

        {recentWithMeta.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-10"
          >
            <div className="flex items-center gap-2 mb-4">
              <Clock className="h-5 w-5" style={{ color: 'var(--accent)' }} />
              <h3 className="text-lg font-bold" style={{ color: 'var(--text-primary)' }}>
                {t('games.continue')}
              </h3>
            </div>
            <div className="flex flex-wrap gap-3">
              {recentWithMeta.map((game) => (
                <button
                  key={game.id}
                  type="button"
                  onClick={() => handleGameClick(game)}
                  className="shell-chip shell-chip-active min-w-[8rem] text-left"
                >
                  <span className="block font-bold">{game.title}</span>
                  <span className="block text-xs opacity-80">
                    {game.stats.bestScore
                      ? t('progress.score', { score: game.stats.bestScore })
                      : t('progress.plays', { count: game.stats.plays || 0 })}
                  </span>
                </button>
              ))}
            </div>
          </motion.div>
        )}

        {achievements.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-10"
          >
            <h3 className="text-lg font-bold mb-3" style={{ color: 'var(--text-primary)' }}>
              {t('achievements.title')}
            </h3>
            <div className="flex flex-wrap gap-2">
              {achievements.map((badge) => (
                <span key={badge.id} className="shell-chip shell-chip-active">
                  {t(`achievements.${badge.id}`)}
                </span>
              ))}
            </div>
          </motion.div>
        )}

        <div className="flex flex-wrap gap-2 mb-8">
          {categories.map((cat) => (
            <button
              key={cat.id}
              type="button"
              onClick={() => setActiveCategory(cat.id)}
              className={`shell-chip ${activeCategory === cat.id ? 'shell-chip-active' : ''}`}
            >
              {cat.label}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
          {filteredGames.map((game, index) => (
            <motion.button
              key={game.id}
              type="button"
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.04 }}
              viewport={{ once: true }}
              className="shell-card p-4 text-left w-full group min-h-[44px]"
              onClick={() => handleGameClick(game)}
            >
              <div
                className="inline-flex items-center justify-center w-14 h-14 rounded-lg mb-3"
                style={{ background: 'var(--accent-soft)', color: 'var(--accent)' }}
              >
                {game.icon}
              </div>
              <h3 className="text-xl font-bold mb-2" style={{ color: 'var(--text-primary)' }}>
                {game.title}
              </h3>
              <p className="text-sm mb-4 leading-relaxed" style={{ color: 'var(--text-secondary)' }}>
                {game.description}
              </p>
              <div className="flex items-center justify-between gap-3">
                <span
                  className="px-3 py-1 rounded-full text-xs font-bold"
                  style={{ background: 'var(--accent-soft)', color: 'var(--accent)' }}
                >
                  {game.difficulty}
                </span>
                <span className="shell-button-secondary text-sm px-4 py-2 min-h-[44px]">
                  {t('game.play')}
                </span>
              </div>
            </motion.button>
          ))}
        </div>
      </div>
    </section>
  );
};

export default GamesSection;
